import { defineStore } from 'pinia'
import axios from 'axios'
import { createToaster } from '@meforma/vue-toaster'
import { router } from '@inertiajs/vue3'
import { debounce } from 'lodash'

const toaster = createToaster({ position: 'bottom' })

export const useTransactionsStore = defineStore('transactionsStore', {
    state: () => ({
        filterData: {
            types: {
                data: []
            },
            categories: {
                income: {
                    data: [],
                    current_page: 0,
                    last_page: null
                },
                expense: {
                    data: [],
                    current_page: 0,
                    last_page: null
                }
            },
            counterparties: {
                client_supplier: {
                    data: [],
                    current_page: 0,
                    last_page: null
                },
                investor: {
                    data: [],
                    current_page: 0,
                    last_page: null
                },
                employee: {
                    data: [],
                    current_page: 0,
                    last_page: null
                },
                borrower: {
                    data: [],
                    current_page: 0,
                    last_page: null
                },
                creditor: {
                    data: [],
                    current_page: 0,
                    last_page: null
                },
                owner: {
                    data: [],
                    current_page: 0,
                    last_page: null
                },
                type_of_taxes: {
                    data: [],
                    current_page: 0,
                    last_page: null
                }
            },
            labels: {
                data: [],
                current_page: 0,
                last_page: null
            },
            users: {
                activeUsers: [],
                searchResult: []
            },
            breadcrumbs: [],
            accountsGroups: [],
            accounts: [],
            expandedAccountGroups: {},
            accountsFirstTimeLoad: true,
            isAccountsLoading: false,
            loadingFilters: []
        },
        onlyActiveFilters: {
            accounts: [],
            type: [],
            categories: {
                income: [],
                expense: []
            },
            counterparties: {},
            labels: [],
            users: []
        },
        selectedFilters: {
            accounts: [],
            date: [],
            types: [],
            categories: {
                income: [],
                expense: []
            },
            counterparties: {
                client_supplier: [],
                investor: [],
                employee: [],
                borrower: [],
                creditor: [],
                owner: [],
                type_of_taxes: []
            },
            labels: [],
            users: [],
            category_types: [],
            counterparty_types: []
        },
        availableFilters: {
            category_types: [],
            counterparty_types: []
        },
        activeSavedFilterSlug: '',
        savedFilters: [],
        transactionsData: {
            tableData: [],
            generalBalance: '',
            income: '',
            expense: '',
            sort: {
                field: null,
                value: null
            },
            page: 1,
            perPage: 10,
            totalRows: 1,
            loading: true
        }
    }),

    actions: {
        async loadFilterData(
            filterName,
            filterParentName,
            requestRouteName,
            searchQuery,
            activeOnlyKey
        ) {
            let pageNum = 1

            if (filterParentName?.length) {
                if (!this.filterData[filterName][filterParentName]) {
                    this.filterData[filterName][filterParentName] = {
                        data: [],
                        current_page: 0,
                        last_page: 1
                    }
                }

                const { current_page, last_page } = this.filterData[filterName][filterParentName]

                if (current_page === last_page) return

                pageNum = current_page + 1
            }

            this.filterData.loadingFilters.push(filterName)

            try {
                const payload = {
                    filterParentName,
                    search: searchQuery,
                    page: pageNum
                }

                if (activeOnlyKey !== null) {
                    payload[activeOnlyKey] = this.selectedFiltersMerged()[filterName]
                }

                let response

                try {
                    response = await axios.post(
                        route(`company.transactions.${requestRouteName}`),
                        payload
                    )
                } catch (error) {
                    const errorMessage =
                        error.response?.data?.message || error.message || 'An error occurred'

                    this.$toast.error(errorMessage)
                    console.error(error)
                }

                const paginator = response?.data?.data

                if (paginator) {
                    if (filterParentName?.length) {
                        const parentData = paginator[filterParentName]

                        Object.assign(this.filterData[filterName][filterParentName], {
                            current_page: parentData.current_page,
                            last_page: parentData.last_page,
                            to: parentData.to,
                            total: parentData.total,
                            data: [
                                ...this.filterData[filterName][filterParentName].data,
                                ...parentData.data
                            ]
                        })
                    } else {
                        this.filterData[filterName] = paginator
                    }
                } else {
                    console.error('Paginator structure is missing in response:', response)
                }
            } catch (error) {
                console.error('Error loading data:', error)
            } finally {
                this.filterData.loadingFilters = this.filterData.loadingFilters.filter(
                    (item) => item !== filterName
                )
            }
        },

        async loadTransactions() {
            console.log('load transactions with filter:', this.selectedFiltersMerged())
            this.transactionsData.loading = true
            axios
                .post(route('company.transactions.loadList'), {
                    page: this.transactionsData.page,
                    sort: {
                        field: this.transactionsData.sort.field,
                        value: this.transactionsData.sort.value
                            ? this.transactionsData.sort.value.toUpperCase()
                            : null
                    },
                    filters: this.selectedFiltersMerged()
                })
                .then((response) => {
                    console.log(response)
                    this.loadIncomeExpense()
                    this.transactionsData.tableData = response.data.data.data
                    this.transactionsData.totalRows = response.data.data.total
                    this.transactionsData.perPage = response.data.data.per_page
                })
                .catch((error) => {
                    const errorMessage =
                        error.response?.data?.message || error.message || 'An error occurred'

                    this.$toast.error(errorMessage)
                })
                .finally(() => {
                    this.transactionsData.loading = false
                })
        },

        loadGeneralBalance() {
            axios
                .post(route('company.loadGeneralBalance'))
                .then((response) => {
                    const result = response.data.data

                    this.transactionsData.generalBalance = result.balance
                })
                .catch((error) => {
                    const errorMessage =
                        error.response?.data?.message || error.message || 'An error occurred'

                    this.$toast.error(errorMessage)
                })
        },

        loadIncomeExpense() {
            axios
                .post(route('company.transactions.loadIncomeAndExpense'), {
                    filters: this.selectedFiltersMerged()
                })
                .then((response) => {
                    const result = response.data.data

                    this.transactionsData.income = result.income
                    this.transactionsData.expense = result.expense
                })
                .catch((error) => {
                    const errorMessage =
                        error.response?.data?.message || error.message || 'An error occurred'

                    this.$toast.error(errorMessage)
                })
        },

        loadAccountsAndGroups(slug = null, callback = null) {
            this.filterData.isAccountsLoading = true

            axios
                .post(route('company.transactions.getLeftSideUserFinancialAccounts'), {
                    financial_account_group_slug: slug,
                    selected_financial_account_slugs: this.selectedFilters.accounts
                })
                .then(async (response) => {
                    const { breadcrumbs, groups, accounts } = response.data.data

                    this.filterData.breadcrumbs = breadcrumbs
                    this.filterData.accountsGroups = groups
                    this.filterData.accounts = accounts

                    const expandedSlugs = Object.keys(this.filterData.expandedAccountGroups)
                    if (expandedSlugs.length) {
                        await Promise.all(
                            expandedSlugs.map((expandedSlug) =>
                                this.loadExpandedAccountGroup(expandedSlug)
                            )
                        )
                    }

                    if (this.filterData.accountsFirstTimeLoad) {
                        if (callback) {
                            callback()
                        }
                        this.filterData.accountsFirstTimeLoad = false
                    }
                })
                .catch((error) => {
                    const errorMessage =
                        error.response?.data?.message || error.message || 'An error occurred'

                    this.$toast.error(errorMessage)
                })
                .finally(() => {
                    this.filterData.isAccountsLoading = false
                })
        },

        async loadExpandedAccountGroup(slug) {
            const response = await axios.post(
                route('company.transactions.getLeftSideUserFinancialAccounts'),
                {
                    financial_account_group_slug: slug,
                    selected_financial_account_slugs: this.selectedFilters.accounts
                }
            )

            const { groups, accounts } = response.data.data
            const children = {
                groups: groups || [],
                accounts: accounts || []
            }

            this.filterData.expandedAccountGroups = {
                ...this.filterData.expandedAccountGroups,
                [slug]: children
            }

            return children
        },

        async toggleExpandedAccountGroup(slug) {
            if (this.filterData.expandedAccountGroups[slug]) {
                const expanded = { ...this.filterData.expandedAccountGroups }
                delete expanded[slug]
                this.filterData.expandedAccountGroups = expanded
                return null
            }

            return this.loadExpandedAccountGroup(slug)
        },

        getExpandedAccountGroup(slug) {
            return (
                this.filterData.expandedAccountGroups[slug] || {
                    groups: [],
                    accounts: []
                }
            )
        },

        isAccountGroupExpanded(slug) {
            return Boolean(this.filterData.expandedAccountGroups[slug])
        },

        toggleParentFilterItem(parentKey, filterName, selectedParentFilterKey) {
            if (
                this.selectedFilters[filterName][parentKey].length ===
                this.filterData[filterName][parentKey].total
            ) {
                this.selectedFilters[filterName][parentKey] = []

                return
            }

            const index = this.selectedFilters[selectedParentFilterKey].indexOf(parentKey)

            if (index === -1) {
                this.selectedFilters[selectedParentFilterKey].push(parentKey)
                this.selectedFilters[filterName][parentKey] = []
            } else {
                this.selectedFilters[selectedParentFilterKey].splice(index, 1)
            }

            this.filterChangeHandler(true)
        },

        toggleFilter(filterItem, filterName, parent = null, filterSelectKey = 'id') {
            const targetArray = parent
                ? this.selectedFilters[filterName][parent]
                : this.selectedFilters[filterName]

            const index = targetArray.indexOf(filterItem[filterSelectKey])

            if (index === -1) {
                targetArray.push(filterItem[filterSelectKey])
            } else {
                targetArray.splice(index, 1)
            }

            this.filterChangeHandler(true)
        },

        filterChangeHandler(withSavedFilterReset = false) {
            if (withSavedFilterReset && this.activeSavedFilterSlug?.length) {
                this.activeSavedFilterSlug = ''
                router.visit(route('company.dashboard'), {
                    preserveState: true,
                    preserveScroll: true
                })
            }

            this.debouncedLoadTransactions()
        },

        debouncedLoadTransactions: debounce(function () {
            this.loadTransactions()
        }, 500),

        clearSpecificFilter(filterName) {
            this.selectedFilters[filterName] = []
        },

        clearMultilevelSpecificFilter(filterName) {
            Object.keys(this.selectedFilters[filterName]).forEach((key) => {
                this.selectedFilters[filterName][key] = []
            })
        },

        saveFilters(filterName) {
            axios
                .post(route('company.transactions.createFilter'), {
                    name: filterName,
                    ...this.selectedFiltersMerged()
                })
                .then((response) => {
                    const result = response.data?.data

                    if (result) {
                        this.savedFilters.push(result)
                    }
                })
                .catch((error) => {
                    const errorMessage =
                        error.response?.data?.message || error.message || 'An error occurred'

                    this.$toast.error(errorMessage)
                })
        },

        deleteSavedFilters(slug) {
            return axios
                .delete(
                    route('company.transactions.deleteFilter', {
                        slug
                    })
                )
                .then(() => {
                    this.savedFilters = this.savedFilters.filter((item) => item.slug !== slug)
                })
                .catch((error) => {
                    const errorMessage =
                        error.response?.data?.message || error.message || 'An error occurred'

                    this.$toast.error(errorMessage)
                })
        },

        selectedFiltersMerged() {
            return {
                accounts: this.selectedFilters.accounts,
                date: this.selectedFilters.date,
                types: this.selectedFilters.types,
                categories: [
                    ...this.selectedFilters.categories.income,
                    ...this.selectedFilters.categories.expense
                ],
                counterparties: [
                    ...this.selectedFilters.counterparties.client_supplier,
                    ...this.selectedFilters.counterparties.investor,
                    ...this.selectedFilters.counterparties.employee,
                    ...this.selectedFilters.counterparties.borrower,
                    ...this.selectedFilters.counterparties.creditor,
                    ...this.selectedFilters.counterparties.owner,
                    ...this.selectedFilters.counterparties.type_of_taxes
                ],
                labels: this.selectedFilters.labels,
                users: this.selectedFilters.users,
                category_types: this.selectedFilters.category_types,
                counterparty_types: this.selectedFilters.counterparty_types
            }
        },

        async filterCheckAvailableOptions() {
            let response

            try {
                response = await axios.post(route(`company.transactions.checkFilter`), {
                    types: this.selectedFiltersMerged().types,
                    categories: this.selectedFiltersMerged().categories,
                    category_types: this.selectedFiltersMerged().category_types,
                    counterparties: this.selectedFiltersMerged().counterparties,
                    counterparty_types: this.selectedFiltersMerged().counterparty_types
                })
            } catch (error) {
                const errorMessage =
                    error.response?.data?.message || error.message || 'An error occurred'

                this.$toast.error(errorMessage)
                console.error(error)
            }

            const availableOptions = response?.data

            for (const key in availableOptions) {
                const availableValues = availableOptions[key]

                if (Array.isArray(this.selectedFilters[key])) {
                    this.selectedFilters[key] = this.selectedFilters[key].filter((item) =>
                        availableValues?.includes(item)
                    )
                    this.availableFilters[key] = availableValues
                } else if (
                    typeof this.selectedFilters[key] === 'object' &&
                    this.selectedFilters[key] !== null
                ) {
                    for (const subKey in this.selectedFilters[key]) {
                        this.selectedFilters[key][subKey] = this.selectedFilters[key][
                            subKey
                        ].filter((item) => availableValues?.includes(item))
                    }
                }
            }
        },

        updateSelectedFilters(data) {
            const updateObject = (target, source) => {
                Object.keys(target).forEach((key) => {
                    target[key] = source && Array.isArray(source[key]) ? source[key] : []
                })
            }

            this.selectedFilters.types = Array.isArray(data.types) ? data.types : []
            updateObject(this.selectedFilters.categories, data.categories)
            updateObject(this.selectedFilters.counterparties, data.counterparties)
            this.selectedFilters.labels = Array.isArray(data.labels) ? data.labels : []

            this.selectedFilters.users = Array.isArray(data.users)
                ? data.users.map((user) => user.id)
                : []

            this.filterData.users.activeUsers = Array.isArray(data.users) ? data.users : []

            this.selectedFilters.category_types = Array.isArray(data.category_types)
                ? data.category_types
                : []
            this.selectedFilters.counterparty_types = Array.isArray(data.counterparty_types)
                ? data.counterparty_types
                : []
        },

        updateFilterAccountAndGroups(data) {
            const accountsData = Array.isArray(data.accounts) ? data.accounts : []

            this.selectedFilters.accounts = accountsData
            this.filterData.accounts.forEach((account) => {
                account.status = accountsData.includes(account.slug) ? 'full' : 'empty'
            })

            if (data.groups && typeof data.groups === 'object') {
                Object.entries(data.groups).forEach(([key, value]) => {
                    const group = this.filterData.accountsGroups.find((item) => item.slug === key)

                    if (group) group.status = value
                })
            } else {
                this.filterData.accountsGroups.forEach((group) => (group.status = 'empty'))
            }
        },

        async updateDashboardInfo(withResetFilters = false, withAccountsAndGroups = true) {
            if (withResetFilters) {
                this.resetFilters()
            }

            this.loadGeneralBalance()

            if (withAccountsAndGroups) {
                this.loadAccountsAndGroups()
            }

            await this.loadTransactions()
        },

        resetFilters() {
            this.selectedFilters = {
                accounts: [],
                date: this.selectedFilters.date,
                types: [],
                categories: {
                    income: [],
                    expense: []
                },
                counterparties: {
                    client_supplier: [],
                    investor: [],
                    employee: [],
                    borrower: [],
                    creditor: [],
                    owner: [],
                    type_of_taxes: []
                },
                labels: [],
                users: [],
                category_types: [],
                counterparty_types: []
            }

            this.updateFilterAccountAndGroups({})
        }
    }
})
